"use client"

import { ChakraProvider } from "@chakra-ui/react"
import createCache from "@emotion/cache"
import { CacheProvider } from "@emotion/react"
import { MotionConfig, useReducedMotion } from "framer-motion"
import dynamic from "next/dynamic"
import { useServerInsertedHTML } from "next/navigation"
import { type ReactNode, useEffect, useReducer, useState } from "react"

import { NavigationProvider } from "@/context/NavigationContext"
import { AuthProvider } from "@/contexts/AuthContext"
import { system } from "@/theme"

import { setupBfCacheOptimization } from "./bfcache-optimization"

// Defer UTMTracker so it doesn't block hydration / initial paint.
const UTMTracker = dynamic(
  () => import("@/components/UTMTracker/UTMTracker").then((m) => m.UTMTracker),
  { loading: () => null, ssr: false }
)

function EmotionCacheProvider({ children }: { children: ReactNode }) {
  const [{ cache, flush }] = useState(() => {
    const emotionCache = createCache({ key: "css", prepend: true })
    emotionCache.compat = true
    const previousInsert = emotionCache.insert
    let insertedNames: string[] = []

    emotionCache.insert = (...args) => {
      const serialized = args[1]

      if (emotionCache.inserted[serialized.name] === undefined) {
        insertedNames.push(serialized.name)
      }

      return previousInsert(...args)
    }

    return {
      cache: emotionCache,
      flush: () => {
        const names = insertedNames
        insertedNames = []

        return names
      },
    }
  })

  useServerInsertedHTML(() => {
    const names = flush()
    const styles = names
      .reduce<string[]>((insertedStyles, name) => {
        const style = cache.inserted[name]

        if (typeof style === "string") {
          insertedStyles.push(style)
        }

        return insertedStyles
      }, [])
      .join("")

    if (!styles) {
      return null
    }

    return (
      <style data-emotion={`${cache.key} ${names.join(" ")}`}>{styles}</style>
    )
  })

  return <CacheProvider value={cache}>{children}</CacheProvider>
}

export function Providers({ children }: { children: ReactNode }) {
  const prefersReducedMotion = useReducedMotion()
  const [hydrationState, dispatchHydrationState] = useReducer(
    (
      state: { idle: boolean; mounted: boolean },
      action: "mark-idle" | "mark-mounted"
    ) => {
      switch (action) {
        case "mark-idle":
          return { ...state, idle: true }
        case "mark-mounted":
          return { ...state, mounted: true }
        default:
          return state
      }
    },
    { idle: false, mounted: false }
  )

  useEffect(() => {
    setupBfCacheOptimization()
    dispatchHydrationState("mark-mounted")

    // Defer non-critical work (UTM tracking) to browser idle.
    type IdleWindow = Window & {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout: number }
      ) => number
      cancelIdleCallback?: (handle: number) => void
    }
    const w = window as IdleWindow
    let idleHandle: number | undefined
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined

    if (typeof w.requestIdleCallback === "function") {
      idleHandle = w.requestIdleCallback(
        () => dispatchHydrationState("mark-idle"),
        {
          timeout: 2000,
        }
      )
    } else {
      timeoutHandle = setTimeout(
        () => dispatchHydrationState("mark-idle"),
        1500
      )
    }

    return () => {
      if (
        idleHandle !== undefined &&
        typeof w.cancelIdleCallback === "function"
      ) {
        w.cancelIdleCallback(idleHandle)
      }
      if (timeoutHandle !== undefined) {
        clearTimeout(timeoutHandle)
      }
    }
  }, [])

  return (
    <EmotionCacheProvider>
      <ChakraProvider value={system}>
        <MotionConfig reducedMotion={prefersReducedMotion ? "always" : "user"}>
          <AuthProvider>
            <NavigationProvider>
              {hydrationState.mounted && hydrationState.idle && <UTMTracker />}
              {children}
            </NavigationProvider>
          </AuthProvider>
        </MotionConfig>
      </ChakraProvider>
    </EmotionCacheProvider>
  )
}
